import { useAuth } from "@contexts/auth";
import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { EngagementDashboard } from "@customTypes/accountManagement";
import { DoctorProfileFunnelRow, PaymentDiagnosticDevice, PaymentDiagnosticLanguage,PaymentDiagnostics } from "@customTypes/dashboard";

import { DoctorFunnelPlatform } from "./useGetDoctorProfileActivity";


const mapFunnel = (funnel: any) => ({
  doctorProfileViewed: funnel?.doctor_profile_viewed ?? 0,
  serviceSelected: funnel?.service_selected ?? 0,
  slotSelected: funnel?.slot_selected ?? 0,
  bookingStarted: funnel?.booking_started ?? 0,
  bookingCompleted: funnel?.booking_completed ?? 0,
  paymentStarted: funnel?.payment_started ?? 0,
  paymentCompleted: funnel?.payment_completed ?? 0,
});

export interface DoctorFunnelResponse {
  trackingStartedAt?: string;
  totals: ReturnType<typeof mapFunnel>;
  doctors: DoctorProfileFunnelRow[];
  conversionRates: { viewToBook: number; bookToPay: number };
  breakdowns: Record<string, { value: string; count: number }[]>;
}

export const useGetDoctorFunnel = (
  days: number,
  platform: DoctorFunnelPlatform
) => {
  const { id, permissions } = useAuth();
  return useQuery({
    queryKey: ["dashboard", "doctorFunnel", days, platform, id, permissions["reports.view"]],
    queryFn: () =>
      api.get("analytics/doctor-funnel", { params: { days, platform } }),
    select: (response: any): DoctorFunnelResponse => ({
      trackingStartedAt: response.data.tracking_started_at,
      totals: mapFunnel(response.data.totals),
      doctors: (response.data.doctors ?? []).map((doctor: any) => ({
        doctorId: doctor.doctor_id,
        profileId: doctor.profile_id,
        slug: doctor.slug,
        name: doctor.name,
        profileImage: doctor.profile_image,
        specialization: doctor.specialization,
        funnel: mapFunnel(doctor.funnel),
      })),
      conversionRates: {
        viewToBook: response.data.conversion_rates?.view_to_book ?? 0,
        bookToPay: response.data.conversion_rates?.book_to_pay ?? 0,
      },
      breakdowns: response.data.breakdowns ?? {},
    }),
    staleTime: 60_000,
  });
};

export const useGetDoctorEngagement = (days: 7 | 30 | 90, enabled: boolean) =>
  useQuery({
    queryKey: ["dashboard", "doctorEngagement", days],
    queryFn: async () => {
      const { data } = await api.get<EngagementDashboard>(
        "account-management/engagement/me",
        { params: { date_range: `${days}d` } }
      );
      return data;
    },
    enabled,
    staleTime: 60_000,
  });


export const useGetPaymentDiagnostics = (
  days: number,
  platform: DoctorFunnelPlatform,
  filters: { doctorId?: string; device?: PaymentDiagnosticDevice; language?: PaymentDiagnosticLanguage },
) => {
  const { id, permissions } = useAuth();
  return useQuery({
    queryKey: ["dashboard", "paymentDiagnostics", days, platform, filters, id, permissions["reports.view"]],
    queryFn: async () => {
      const { data } = await api.get<PaymentDiagnostics>("analytics/doctor-funnel/payments", {
        params: { days, platform, doctor_id: filters.doctorId, device: filters.device, language: filters.language },
      });
      return data;
    },
    staleTime: 60_000,
  });
};
